import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BASE } from '../services/api';
import { useToast } from '../context/ToastContext';
import { baixarArquivo, gerarCsv, gerarXlsx, TIPO_XLSX } from '../services/planilha';
import '../assets/css/GestaoClientesApp.css';

/* Contas do app do cliente (proprietários e compradores): diretoria e administração
 * consultam, corrigem cadastro, redefinem senha, desativam e excluem. As rotas ficam em
 * /portal-admin e conferem a permissão no banco — esconder o menu não é a proteção.
 *
 * Desde 28/10/2026 a sincronização traz TODO proprietário (venda) e locador (aluguel) do Imoview, mesmo com
 * telefone errado ou sem telefone/CPF. Esses aparecem com o que corrigir ("Dados a
 * corrigir") e saem na planilha para a equipe consertar no Imoview. */

const FILTROS = [
  ['', 'Todos'], ['ativou', 'Já usam o app'], ['pendente', 'Não ativaram'], ['corrigir', 'Dados a corrigir'],
  ['sem_telefone', 'Sem telefone'], ['desativado', 'Desativados'], ['exclusao', 'Pediram exclusão'],
];

const LINK_IMOVEL = 'https://app.imoview.com.br/Imovel/Detalhes/';

/** Linhas da planilha de cadastros a corrigir: uma por cliente, imóveis juntos na célula. */
export function linhasACorrigir(itens) {
  const juntar = (lista) => lista.filter(Boolean).join('; ');
  const cabecalho = ['Nome', 'Cliente no Imoview', 'O que corrigir', 'Telefone no Imoview', 'E-mail',
    'Imóveis', 'Vagos/disponíveis', 'Situação dos imóveis', 'Endereço', 'Captador(es)', 'Link do imóvel', 'Nº no app'];
  const linhas = itens.map((c) => {
    const imoveis = c.imoveis || [];
    return [
      c.nome, c.codigo_imoview || '', juntar(c.pendencias.map((p) => p.rotulo)), c.telefone_imoview || '',
      c.email || '', juntar(imoveis.map((i) => i.codigo)),
      juntar(imoveis.filter((i) => i.disponivel).map((i) => i.codigo)),
      juntar(imoveis.map((i) => {
        const partes = [i.finalidade, i.situacao].filter(Boolean).join(' · ');
        return partes ? `${i.codigo}: ${partes}` : null;
      })),
      juntar([...new Set(imoveis.map((i) => i.endereco))]),
      juntar([...new Set(imoveis.map((i) => i.captador))]),
      imoveis.length ? `${LINK_IMOVEL}${(imoveis.find((i) => i.disponivel) || imoveis[0]).codigo}` : '',
      String(c.id),
    ];
  });
  return { linhas: [cabecalho, ...linhas], larguras: [32, 14, 30, 22, 26, 18, 18, 30, 34, 26, 44, 9] };
}

function Pendencias({ itens }) {
  if (!itens || !itens.length) return null;
  return (
    <span className="ca-pends">
      {itens.map((p) => (
        // Telefone impede o login (laranja); CPF/CNPJ só falta no cadastro (cinza).
        <span key={p.codigo} className={`ca-pend${p.codigo.includes('documento') ? ' is-leve' : ''}`}>
          {p.rotulo}
        </span>
      ))}
    </span>
  );
}

const telefoneBR = (t) => {
  if (!t) return '—';
  const n = String(t).replace(/^55/, '');
  return n.length >= 10 ? `(${n.slice(0, 2)}) ${n.slice(2, -4)}-${n.slice(-4)}` : t;
};
const documentoBR = (d) => {
  if (!d) return '—';
  if (d.length === 11) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
  if (d.length === 14) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
  return d;
};
const dataBR = (v) => (v ? new Date(v).toLocaleDateString('pt-BR') : '—');

function situacao(c) {
  if (!c.ativo) return ['Desativado', 'is-off'];
  if (c.exclusao_solicitada_em) return ['Pediu exclusão', 'is-alerta'];
  if (!c.telefone) return ['Sem telefone', 'is-alerta'];
  return c.ativou ? ['Usa o app', 'is-ok'] : ['Não ativou', 'is-pendente'];
}

async function chamar(caminho, { method = 'GET', body } = {}) {
  const r = await fetch(`${BASE}/portal-admin${caminho}`, {
    method,
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || 'Não foi possível concluir.');
  return d;
}

export default function GestaoClientesApp() {
  const toast = useToast();
  const [busca, setBusca] = useState('');
  const [buscaAtiva, setBuscaAtiva] = useState('');
  const [filtro, setFiltro] = useState('');
  const [pagina, setPagina] = useState(1);
  const [lista, setLista] = useState({ itens: [], total: 0, paginas: 1 });
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [aberto, setAberto] = useState(null);      // id da ficha aberta
  const [novo, setNovo] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true); setErro('');
    try {
      const qs = new URLSearchParams({ pagina: String(pagina) });
      if (buscaAtiva) qs.set('busca', buscaAtiva);
      if (filtro) qs.set('filtro', filtro);
      setLista(await chamar(`/clientes?${qs}`));
    } catch (e) {
      setErro(e.message);
    } finally {
      setCarregando(false);
    }
  }, [pagina, buscaAtiva, filtro]);

  useEffect(() => { carregar(); }, [carregar]);

  const buscar = (e) => { e.preventDefault(); setPagina(1); setBuscaAtiva(busca.trim()); };

  const exportarRef = useRef(null);
  const [soDisponiveis, setSoDisponiveis] = useState(true);
  const [comCpf, setComCpf] = useState(false);
  const [exportando, setExportando] = useState(false);

  const exportar = async (formato) => {
    setExportando(true);
    try {
      const qs = new URLSearchParams();
      if (soDisponiveis) qs.set('disponiveis', '1');
      if (comCpf) qs.set('cpf', '1');
      const { itens } = await chamar(`/clientes/a-corrigir?${qs}`);
      if (!itens.length) { toast('Nenhum cadastro a corrigir com esses critérios.', 'info'); return; }
      const { linhas, larguras } = linhasACorrigir(itens);
      const nome = `clientes-app-a-corrigir-${new Date().toISOString().slice(0, 10)}`;
      if (formato === 'xlsx') baixarArquivo(gerarXlsx(linhas, { aba: 'A corrigir', larguras }), `${nome}.xlsx`, TIPO_XLSX);
      else baixarArquivo(gerarCsv(linhas), `${nome}.csv`, 'text/csv;charset=utf-8');
      if (exportarRef.current) exportarRef.current.open = false;
      toast(`${itens.length} cliente(s) na planilha.`, 'success');
    } catch (e) {
      toast(e.message, 'error');
    } finally {
      setExportando(false);
    }
  };

  return (
    <div className="ca-page">
      <header className="ca-hero">
        <div>
          <span className="ca-eyebrow"><i />Administração</span>
          <h1>Clientes do App</h1>
          <p>Proprietários e compradores com conta no app do cliente. O login é o telefone; a senha
            quem cria é o próprio cliente — redefinir apaga a senha e ele cria outra no app.
            Todo proprietário (venda) e locador (aluguel) do Imoview entra aqui, mesmo com cadastro incompleto.</p>
        </div>
        <div className="ca-hero-acoes">
          <details className="ca-exportar" ref={exportarRef}>
            <summary className="ca-sec">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M12 4v11M7 10l5 5 5-5M5 19h14" /></svg>
              Exportar a corrigir <span aria-hidden="true">▾</span>
            </summary>
            <div className="ca-exportar-menu">
              <strong>Clientes sem telefone ou com telefone incorreto</strong>
              <small>Com o telefone como está no Imoview, os imóveis e o captador — para corrigir lá.</small>
              <label>
                <input type="checkbox" checked={soDisponiveis} onChange={(e) => setSoDisponiveis(e.target.checked)} />
                <span>Só quem tem imóvel vago/disponível</span>
              </label>
              <label>
                <input type="checkbox" checked={comCpf} onChange={(e) => setComCpf(e.target.checked)} />
                <span>Incluir quem só está sem CPF/CNPJ ou com ele incorreto</span>
              </label>
              <div className="ca-exportar-botoes">
                <button type="button" className="ca-cta" disabled={exportando} onClick={() => exportar('xlsx')}>
                  {exportando ? 'Gerando…' : 'Excel (.xlsx)'}
                </button>
                <button type="button" className="ca-sec" disabled={exportando} onClick={() => exportar('csv')}>CSV</button>
              </div>
            </div>
          </details>
          <button type="button" className="ca-cta" onClick={() => setNovo(true)}>Novo cliente</button>
        </div>
      </header>

      <form className="ca-busca" onSubmit={buscar}>
        <input value={busca} onChange={(e) => setBusca(e.target.value)} aria-label="Buscar cliente"
          placeholder="Nome, telefone, CPF, e-mail ou código do imóvel" />
        <button type="submit" className="ca-cta">Buscar</button>
        {buscaAtiva && (
          <button type="button" className="ca-sec" onClick={() => { setBusca(''); setBuscaAtiva(''); setPagina(1); }}>Limpar</button>
        )}
        <span className="ca-contador">{carregando ? 'Carregando…' : `${lista.total.toLocaleString('pt-BR')} cliente(s)`}</span>
      </form>

      <div className="ca-chips" role="tablist">
        {FILTROS.map(([valor, rotulo]) => (
          <button key={valor || 'todos'} type="button" role="tab" aria-selected={filtro === valor}
            className={filtro === valor ? 'is-ativo' : ''} onClick={() => { setFiltro(valor); setPagina(1); }}>
            {rotulo}
          </button>
        ))}
      </div>

      {erro && <div className="ca-erro" role="alert">{erro}</div>}

      <div className="ca-tabela-wrap">
        <table className="ca-tabela">
          <thead>
            <tr><th>Nome</th><th>Telefone (login)</th><th>CPF/CNPJ</th><th>E-mail</th><th>Imóveis</th><th>Situação</th><th>Cadastro</th></tr>
          </thead>
          <tbody>
            {lista.itens.map((c) => {
              const [rotulo, classe] = situacao(c);
              return (
                <tr key={c.id} onClick={() => setAberto(c.id)} tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') setAberto(c.id); }}>
                  <td className="ca-nome">{c.nome}</td>
                  <td>{telefoneBR(c.telefone)}</td>
                  <td>{documentoBR(c.cpf)}</td>
                  <td className="ca-email">{c.email || '—'}</td>
                  <td>{c.imoveis}</td>
                  <td><span className={`ca-sit ${classe}`}>{rotulo}</span></td>
                  <td>{c.pendencias && c.pendencias.length ? <Pendencias itens={c.pendencias} /> : <span className="ca-ok">Completo</span>}</td>
                </tr>
              );
            })}
            {!carregando && lista.itens.length === 0 && (
              <tr><td colSpan={7} className="ca-vazio">Nenhum cliente encontrado.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {lista.paginas > 1 && (
        <div className="ca-paginacao">
          <button type="button" className="ca-sec" disabled={pagina <= 1} onClick={() => setPagina(pagina - 1)}>Anterior</button>
          <span>Página {pagina} de {lista.paginas}</span>
          <button type="button" className="ca-sec" disabled={pagina >= lista.paginas} onClick={() => setPagina(pagina + 1)}>Próxima</button>
        </div>
      )}

      {aberto && (
        <FichaCliente id={aberto} toast={toast} onFechar={() => setAberto(null)}
          onMudou={carregar} onExcluido={() => { setAberto(null); carregar(); }} />
      )}
      {novo && (
        <NovoCliente toast={toast} onFechar={() => setNovo(false)}
          onCriado={(c) => { setNovo(false); carregar(); setAberto(c.id); }} />
      )}
    </div>
  );
}

function FichaCliente({ id, toast, onFechar, onMudou, onExcluido }) {
  const [ficha, setFicha] = useState(null);
  const [form, setForm] = useState({ nome: '', telefone: '', cpf: '', email: '' });
  const [codigo, setCodigo] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState('');

  const aplicar = (f) => {
    setFicha(f);
    setForm({ nome: f.nome || '', telefone: telefoneBR(f.telefone).replace('—', ''), cpf: f.cpf || '', email: f.email || '' });
  };

  useEffect(() => {
    let vivo = true;
    chamar(`/clientes/${id}`).then((f) => { if (vivo) aplicar(f); }).catch((e) => { if (vivo) setErro(e.message); });
    return () => { vivo = false; };
  }, [id]);

  async function executar(acao, sucesso) {
    setOcupado(true); setErro('');
    try {
      const r = await acao();
      if (r && r.id) aplicar(r);
      toast(sucesso, 'success');
      onMudou();
      return r;
    } catch (e) {
      setErro(e.message);
      return null;
    } finally {
      setOcupado(false);
    }
  }

  const salvar = (e) => {
    e.preventDefault();
    const mudancas = {};
    if (form.nome.trim() !== (ficha.nome || '')) mudancas.nome = form.nome.trim();
    const telDigitos = form.telefone.replace(/\D/g, '');
    if (telDigitos !== String(ficha.telefone || '').replace(/^55/, '')) mudancas.telefone = form.telefone.trim() || null;
    if (form.cpf.replace(/\D/g, '') !== (ficha.cpf || '')) mudancas.cpf = form.cpf.trim() || null;
    if (form.email.trim().toLowerCase() !== (ficha.email || '')) mudancas.email = form.email.trim() || null;
    if (!Object.keys(mudancas).length) { toast('Nada mudou.', 'info'); return; }
    if (mudancas.telefone !== undefined && ficha.ativou
        && !window.confirm('Trocar o telefone muda o login do cliente e encerra as sessões abertas. Continuar?')) return;
    executar(() => chamar(`/clientes/${id}`, { method: 'PATCH', body: mudancas }), 'Cadastro atualizado.');
  };

  const redefinir = () => {
    if (!window.confirm(`Apagar a senha de ${ficha.nome}? No próximo acesso ele digita o telefone e cria uma nova.`)) return;
    executar(() => chamar(`/clientes/${id}/redefinir-senha`, { method: 'POST' }), 'Senha apagada: o cliente cria outra no app.');
  };

  const alternarAtivo = () => {
    const desativar = ficha.ativo;
    if (desativar && !window.confirm(`Desativar o acesso de ${ficha.nome}? Ele sai do app na hora.`)) return;
    executar(() => chamar(`/clientes/${id}`, { method: 'PATCH', body: { ativo: !desativar } }),
      desativar ? 'Acesso desativado.' : 'Acesso reativado.');
  };

  const excluir = async () => {
    const confirmacao = window.prompt(
      `Excluir DEFINITIVAMENTE a conta de ${ficha.nome}? Visitas e propostas continuam no sistema.\n`
      + 'Se ele ainda tiver imóvel à venda no Imoview, a sincronização cria a conta de novo — para barrar o acesso, desative.\n\n'
      + 'Digite EXCLUIR para confirmar.');
    if (confirmacao !== 'EXCLUIR') return;
    const r = await executar(() => chamar(`/clientes/${id}`, { method: 'DELETE' }), 'Conta excluída.');
    if (r) onExcluido();
  };

  const vincular = (e) => {
    e.preventDefault();
    if (!codigo.trim()) return;
    executar(() => chamar(`/clientes/${id}/imoveis`, { method: 'POST', body: { codigo_imovel: codigo.trim() } }),
      `Imóvel ${codigo.trim()} vinculado.`).then((r) => { if (r) setCodigo(''); });
  };

  const desvincular = (cod) => {
    if (!window.confirm(`Tirar o imóvel ${cod} da conta? Se o Imoview ainda o liga a este proprietário, a sincronização vincula de novo.`)) return;
    executar(() => chamar(`/clientes/${id}/imoveis/${encodeURIComponent(cod)}`, { method: 'DELETE' }), `Imóvel ${cod} desvinculado.`);
  };

  return (
    <div className="ca-fundo" role="presentation" onClick={onFechar}>
      <aside className="ca-ficha" role="dialog" aria-modal="true" aria-label="Ficha do cliente" onClick={(e) => e.stopPropagation()}>
        <div className="ca-ficha-topo">
          <div>
            <span className="ca-eyebrow"><i />Cliente #{id}</span>
            <h2>{ficha ? ficha.nome : 'Carregando…'}</h2>
          </div>
          <button type="button" className="ca-fechar" onClick={onFechar} aria-label="Fechar">×</button>
        </div>
        {erro && <div className="ca-erro" role="alert">{erro}</div>}
        {ficha && (
          <>
            <div className="ca-selos">
              <span className={`ca-sit ${situacao(ficha)[1]}`}>{situacao(ficha)[0]}</span>
              {ficha.vinculos.length > 0 && <span className="ca-sit is-neutro">Proprietário</span>}
              {ficha.comprador && <span className="ca-sit is-neutro">Comprador</span>}
            </div>
            {ficha.pendencias && ficha.pendencias.length > 0 && (
              <section className="ca-corrigir" aria-label="Cadastro a corrigir">
                <h3>Cadastro a corrigir</h3>
                <Pendencias itens={ficha.pendencias} />
                <dl className="ca-dados">
                  {ficha.codigo_imoview && <div><dt>Cliente no Imoview</dt><dd>nº {ficha.codigo_imoview}</dd></div>}
                  <div><dt>Telefone no Imoview</dt><dd>{ficha.telefone_imoview || 'vazio'}</dd></div>
                  {ficha.pendencias.some((p) => p.codigo.includes('documento')) && (
                    <div><dt>CPF/CNPJ no Imoview</dt><dd>{ficha.documento_imoview || 'vazio'}</dd></div>
                  )}
                </dl>
                <p className="ca-nota">
                  {ficha.pendencias.some((p) => p.codigo.includes('telefone'))
                    ? 'Sem telefone válido o cliente não consegue entrar no app. '
                    : ''}
                  Corrija no Imoview — a sincronização atualiza aqui em até 1 hora — ou preencha no cadastro abaixo.
                </p>
              </section>
            )}
            <dl className="ca-dados">
              <div><dt>Ativou o app em</dt><dd>{dataBR(ficha.ativado_em)}</dd></div>
              <div><dt>Pedido de exclusão</dt><dd>{ficha.exclusao_solicitada_em
                ? `${dataBR(ficha.exclusao_solicitada_em)}${ficha.exclusao_motivo ? ` — “${ficha.exclusao_motivo}”` : ''}` : '—'}</dd></div>
            </dl>

            <form className="ca-form" onSubmit={salvar}>
              <h3>Cadastro</h3>
              <label><span>Nome</span>
                <input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} maxLength={160} required />
              </label>
              <label><span>Telefone (é o login)</span>
                <input value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} placeholder="(61) 99999-9999" />
              </label>
              <label><span>CPF/CNPJ</span>
                <input value={form.cpf} onChange={(e) => setForm({ ...form, cpf: e.target.value })} placeholder="Opcional" />
              </label>
              <label><span>E-mail</span>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Opcional" />
              </label>
              <button type="submit" className="ca-cta" disabled={ocupado}>{ocupado ? 'Salvando…' : 'Salvar cadastro'}</button>
            </form>

            <section className="ca-bloco">
              <h3>Imóveis nesta conta</h3>
              {ficha.vinculos.length ? (
                <ul className="ca-imoveis">
                  {ficha.vinculos.map((v) => (
                    <li key={v.codigo_imovel}>
                      <span><strong>{v.codigo_imovel}</strong>
                        {(v.finalidade || v.situacao) && (
                          <small className="ca-imovel-info">{[v.finalidade, v.situacao].filter(Boolean).join(' · ')}</small>
                        )}</span>
                      <button type="button" className="ca-link-perigo" disabled={ocupado} onClick={() => desvincular(v.codigo_imovel)}>Desvincular</button>
                    </li>
                  ))}
                </ul>
              ) : <p className="ca-nota">Nenhum imóvel vinculado{ficha.comprador ? ' — entra só como comprador.' : '.'}</p>}
              <form className="ca-linha" onSubmit={vincular}>
                <input value={codigo} onChange={(e) => setCodigo(e.target.value)} placeholder="Código do imóvel" aria-label="Código do imóvel" />
                <button type="submit" className="ca-sec" disabled={ocupado || !codigo.trim()}>Vincular</button>
              </form>
            </section>

            <section className="ca-bloco">
              <h3>Acesso</h3>
              <div className="ca-acoes">
                <button type="button" className="ca-sec" disabled={ocupado || !ficha.ativou} onClick={redefinir}
                  title={ficha.ativou ? '' : 'Ainda não criou senha'}>Redefinir senha</button>
                <button type="button" className="ca-sec" disabled={ocupado} onClick={alternarAtivo}>
                  {ficha.ativo ? 'Desativar acesso' : 'Reativar acesso'}
                </button>
                <button type="button" className="ca-perigo" disabled={ocupado} onClick={excluir}>Excluir conta</button>
              </div>
              <p className="ca-nota">Redefinir apaga a senha: ninguém da equipe fica sabendo a nova. Desativar e trocar o
                telefone encerram as sessões abertas do cliente.</p>
            </section>
          </>
        )}
      </aside>
    </div>
  );
}

function NovoCliente({ toast, onFechar, onCriado }) {
  const [form, setForm] = useState({ nome: '', telefone: '', cpf: '', email: '', imoveis: '' });
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState('');

  const criar = async (e) => {
    e.preventDefault();
    setOcupado(true); setErro('');
    try {
      const corpo = { nome: form.nome.trim(), imoveis: form.imoveis.split(/[\s,;]+/).filter(Boolean) };
      if (form.telefone.trim()) corpo.telefone = form.telefone.trim();
      if (form.cpf.trim()) corpo.cpf = form.cpf.trim();
      if (form.email.trim()) corpo.email = form.email.trim();
      const c = await chamar('/clientes', { method: 'POST', body: corpo });
      toast(corpo.telefone ? 'Cliente criado. Ele entra no app pelo telefone e cria a senha.'
        : 'Cliente criado sem telefone: ele só entra no app quando alguém informar o número.', 'success');
      onCriado(c);
    } catch (err) {
      setErro(err.message);
    } finally {
      setOcupado(false);
    }
  };

  return (
    <div className="ca-fundo" role="presentation" onClick={onFechar}>
      <aside className="ca-ficha" role="dialog" aria-modal="true" aria-label="Novo cliente" onClick={(e) => e.stopPropagation()}>
        <div className="ca-ficha-topo">
          <div><span className="ca-eyebrow"><i />Novo</span><h2>Cliente do app</h2></div>
          <button type="button" className="ca-fechar" onClick={onFechar} aria-label="Fechar">×</button>
        </div>
        <p className="ca-nota">Use quando a sincronização com o Imoview não trouxe o proprietário (ex.: telefone
          diferente no cadastro). A conta nasce sem senha.</p>
        {erro && <div className="ca-erro" role="alert">{erro}</div>}
        <form className="ca-form" onSubmit={criar}>
          <label><span>Nome</span><input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} required maxLength={160} /></label>
          <label><span>Telefone (login)</span><input value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} placeholder="(61) 99999-9999 — sem ele, fica a corrigir" /></label>
          <label><span>CPF/CNPJ</span><input value={form.cpf} onChange={(e) => setForm({ ...form, cpf: e.target.value })} placeholder="Opcional" /></label>
          <label><span>E-mail</span><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Opcional" /></label>
          <label><span>Imóveis (códigos)</span><input value={form.imoveis} onChange={(e) => setForm({ ...form, imoveis: e.target.value })} placeholder="Ex.: 10770, 3123" /></label>
          <button type="submit" className="ca-cta" disabled={ocupado}>{ocupado ? 'Criando…' : 'Criar cliente'}</button>
        </form>
      </aside>
    </div>
  );
}
